import React, { useContext, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';

import FirebaseContext from '../../../../../../../context/FirebaseContext';
import LoggedInUserContext from '../../../../../../../context/LoggedInUserContext';
import ExercisesContext from '../../../../../../context/ExerciseContext';
import UserContext from '../../../../../../../context/UserContext';
import useComponentMounted from '../../../../../../../hooks/useComponentMounted';
import Activity from '../../../../../../../Model/Activity';
import LoadingOverlay from '../../../../../../components/LoadingOverlay';
import useToast from '../../../../../../hooks/useToast';
import MessageInput from '../../../../../../../chat/components/MessageInput';

import {
  ConversationType,
  generateAIWorkout,
  fillMissingExerciseData,
  buildInitialPrompt,
} from './utils';
import { Container } from './styles';
import texts from './texts';

const AIPrompt = ({
  onGenerate,
  conversationHistory,
  currentActivities,
  workoutRequestId,
  generationConfigs,
}) => {
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();
  const { firebase: { remote } } = useContext(FirebaseContext);
  const { exercises } = useContext(ExercisesContext);
  const { userId: loggedInUserId } = useContext(LoggedInUserContext);
  const { userId: coachId } = useContext(UserContext);

  const isInitialGenerationPrompt = !conversationHistory.length && !currentActivities.length;
  const isFirstMessage = !conversationHistory.length;

  /*
  * If it's the first message in the conversation (create or edit), we need to build
  * the initial prompt based on the generation configurations and the additional instructions.
  * Otherwise, we use the prompt as it is.
  */
  const finalPrompt = isFirstMessage ? buildInitialPrompt(generationConfigs, prompt) : prompt;

  const handleGenerate = useCallback(async () => {
    setIsGenerating(true);
    try {
      const {
        success,
        error,
        workout,
        questions,
        requestId,
      } = await generateAIWorkout({
        remote,
        prompt: finalPrompt,
        requestedBy: loggedInUserId,
        coachId,
        // Append conversation history and current activities if it's not the first prompt as a creation
        ...(!isInitialGenerationPrompt && {
          conversationHistory,
          currentActivities: currentActivities.map((activity) => activity.jsonActivity),
        }),
        ...(workoutRequestId && { workoutRequestId }),
      });

      if (!success) {
        throw new Error(error);
      }

      // New conversation entry for the user's prompt
      const newConversation = [{
        type: ConversationType.USER,
        message: finalPrompt,
        timestamp: new Date().toISOString(),
      }];

      // AI response
      const AIConversation = {
        type: ConversationType.ASSISTANT,
        timestamp: new Date().toISOString(),
      };

      // If the AI needs clarification, add the questions to the conversation
      if (questions) {
        AIConversation.message = questions;
      } else if (workout?.length) {
        // If the AI generated a workout, add the workout to the conversation
        AIConversation.message = `Generated workout with ${workout.length} activities`;
        AIConversation.workout = workout;
      }

      newConversation.push(AIConversation);

      /*
        If the AI generated a workout, process the activities and fill missing exercise data
        If the AI didn't generate a workout, use the current activities
      */
      const processedActivities = workout?.length
        ? workout.map((activity) => fillMissingExerciseData(exercises, activity))
        : currentActivities;
      const activityObjects = processedActivities.map((activity) => new Activity(activity));

      setPrompt('');
      onGenerate(activityObjects, newConversation, requestId);
    } catch (error) {
      showToast(format(texts.errorProcessingWorkout, { error: error.message }), { error: true });
    } finally {
      if (isComponentMountedRef.current) {
        setIsGenerating(false);
      }
    }
  }, [
    remote,
    onGenerate,
    showToast,
    loggedInUserId,
    coachId,
    isComponentMountedRef,
    exercises,
    isInitialGenerationPrompt,
    finalPrompt,
    conversationHistory,
    currentActivities,
    workoutRequestId,
  ]);

  return (
    <Container>
      <MessageInput
        text={prompt}
        placeholder={isFirstMessage ? texts.initialPromptPlaceholder : texts.placeholder}
        onChange={setPrompt}
        onSubmit={handleGenerate}
        isDisabled={!finalPrompt.trim().length || isGenerating}
        isSendingMessage={isGenerating}
        useExtras={false}
        framePadding={0}
        submitOnEnter
      />
      <LoadingOverlay isLoading={isGenerating} loadingText={texts.generating} />
    </Container>
  );
};

AIPrompt.propTypes = {
  onGenerate: PropTypes.func.isRequired,
  conversationHistory: PropTypes.array.isRequired,
  currentActivities: PropTypes.array.isRequired,
  workoutRequestId: PropTypes.string.isRequired,
  generationConfigs: PropTypes.object.isRequired,
};

export default AIPrompt;
